// src/components/PrintReceipt.js

import React from 'react';
import { Button } from '@mui/material';
import { Print } from '@mui/icons-material';
import { formatNumberWithComma } from '../../utils/helpers';
import { companyInfo } from '../../utils/dataInfo';

const PrintReceipt = ({ orderData, disabled = false, sx = {} }) => {
    const handlePrint = () => {
        // Ensure we have items
        const items = orderData.items || [];
        
        // Create printable HTML content optimized for POS printers (80mm)
        const printContent = `
        <div style="font-family: 'Courier New', Courier, monospace; max-width: 300px; margin: 0 auto; padding: 10px; font-size: 12px;">
            <!-- Company Header -->
            <div style="text-align: center; margin-bottom: 10px; border-bottom: 1px dashed #000; padding-bottom: 10px;">
                <h2 style="margin: 0; font-size: 16px; font-weight: bold;">${companyInfo.name}</h2>
                <p style="margin: 2px 0; font-size: 11px;">${companyInfo.address}</p>
                <p style="margin: 2px 0; font-size: 11px;">Tel: ${companyInfo.phone}</p>
                <p style="margin: 2px 0; font-size: 10px; font-weight: bold;">${companyInfo.taxId}</p>
            </div>
            
            <!-- Receipt Title -->
            <div style="text-align: center; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 5px 0; margin: 5px 0;">
                <h3 style="margin: 0; font-size: 14px;">${orderData.orderStatus === 'quotation' ? 'QUOTATION' : 'SALES RECEIPT'}</h3>
            </div>
            
            <!-- Order Info -->
            <div style="margin: 8px 0; font-size: 11px;">
                <div style="display: flex; justify-content: space-between;">
                    <span><strong>Date:</strong> ${new Date(orderData.date || Date.now()).toLocaleString()}</span>
                    <span><strong>#:</strong> ${orderData.id || 'ONSITE'}</span>
                </div>
                ${orderData.customerName ? `<div><strong>Customer:</strong> ${orderData.customerName}</div>` : ''}
                ${orderData.customerPhone ? `<div><strong>Phone:</strong> ${orderData.customerPhone}</div>` : ''}
                ${orderData.orderType === 'loan_order' ? `<div style="color: #e67e22;"><strong>Type:</strong> LOAN ORDER</div>` : ''}
                ${orderData.orderStatus === 'quotation' ? `<div style="color: #3498db;"><strong>Status:</strong> QUOTATION</div>` : ''}
            </div>
            
            <!-- Items Table -->
            <table style="width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px;">
                <thead>
                    <tr style="border-bottom: 2px solid #000;">
                        <th style="text-align: left; padding: 3px 0;">Item</th>
                        <th style="text-align: center; padding: 3px 0;">Qty</th>
                        <th style="text-align: right; padding: 3px 0;">Price</th>
                        <th style="text-align: right; padding: 3px 0;">Total</th>
                    </tr>
                </thead>
                <tbody>
                    ${items.map(item => `
                    <tr style="border-bottom: 1px dotted #ccc;">
                        <td style="padding: 3px 0;">
                            <div>${item.name}</div>
                            ${item.unit_name && item.unit_name !== 'N/A' ? `<div style="font-size: 9px; color: #666;">${item.unit_name}</div>` : ''}
                        </td>
                        <td style="text-align: center; padding: 3px 0;">${formatNumberWithComma(item.quantity)}</td>
                        <td style="text-align: right; padding: 3px 0;">${formatNumberWithComma(item.price)}</td>
                        <td style="text-align: right; padding: 3px 0;">${formatNumberWithComma((item.price * item.quantity))}</td>
                    </tr>
                    `).join('')}
                </tbody>
            </table>
            
            <!-- Totals -->
            <div style="border-top: 2px dashed #000; margin-top: 5px; padding-top: 8px;">
                <div style="display: flex; justify-content: space-between; margin: 3px 0;">
                    <span>Subtotal:</span>
                    <span>${formatNumberWithComma(orderData.total || items.reduce((sum, item) => sum + (item.price * item.quantity), 0))}</span>
                </div>
                ${orderData.customerGivenCash && orderData.orderStatus !== 'quotation' ? `
                <div style="display: flex; justify-content: space-between; margin: 3px 0;">
                    <span>Amount Received:</span>
                    <span>${formatNumberWithComma(parseFloat(orderData.customerGivenCash) || 0)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin: 3px 0; font-weight: bold; font-size: 13px;">
                    <span>Change:</span>
                    <span>${orderData.customerBalance || '0'}</span>
                </div>
                ` : ''}
                ${orderData.orderType === 'loan_order' ? `
                <div style="display: flex; justify-content: space-between; margin: 3px 0; color: #e67e22; font-weight: bold;">
                    <span>Loan Balance:</span>
                    <span>${formatNumberWithComma(orderData.total || items.reduce((sum, item) => sum + (item.price * item.quantity), 0))}</span>
                </div>
                ` : ''}
                <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; margin: 5px 0; border-top: 2px solid #000; padding-top: 5px;">
                    <span>TOTAL:</span>
                    <span>${formatNumberWithComma(orderData.total || items.reduce((sum, item) => sum + (item.price * item.quantity), 0))}</span>
                </div>
            </div>
            
            <!-- Payment Method -->
            <div style="margin: 8px 0; padding: 5px 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; font-size: 11px;">
                <div><strong>Payment:</strong> ${orderData.orderStatus === 'quotation' ? 'QUOTATION' : (orderData.paymentMethod || 'CASH').toUpperCase()}</div>
                ${orderData.orderType === 'loan_order' ? `<div style="color: #e67e22;"><strong>Note:</strong> Loan payment pending</div>` : ''}
            </div>
            
            <!-- Footer -->
            <div style="text-align: center; margin-top: 10px; font-size: 10px; border-top: 1px dashed #000; padding-top: 8px;">
                <p style="margin: 3px 0;">${companyInfo.receiptFooter || 'Thank you for your business!'}</p>
                <p style="margin: 3px 0; font-style: italic;">Items can be exchanged within 7 days with receipt</p>
                ${orderData.orderStatus === 'quotation' ? `<p style="margin: 3px 0; color: #3498db; font-weight: bold;">Quotation valid for 7 days</p>` : ''}
            </div>
        </div>
        `;

        // Open print window optimized for POS printers
        const printWindow = window.open('', '_blank', 'width=300,height=600');
        printWindow.document.write(`
        <html>
            <head>
                <title>${orderData.orderStatus === 'quotation' ? 'Quotation' : 'Receipt'} #${orderData.id || 'ONSITE'}</title>
                <style>
                    @media print {
                        body { 
                            margin: 0;
                            padding: 0;
                            background: white;
                            width: 80mm;
                        }
                        #print-content { 
                            width: 80mm;
                            margin: 0 auto;
                        }
                        @page {
                            size: 80mm auto;
                            margin: 0;
                        }
                    }
                    @media screen {
                        body {
                            padding: 20px;
                            background: #f0f0f0;
                            display: flex;
                            justify-content: center;
                        }
                        #print-content {
                            background: white;
                            padding: 20px;
                            border-radius: 8px;
                            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                            max-width: 80mm;
                        }
                    }
                    body {
                        font-family: 'Courier New', Courier, monospace;
                        margin: 0;
                        padding: 0;
                    }
                    * {
                        box-sizing: border-box;
                    }
                </style>
            </head>
            <body>
                <div id="print-content">
                    ${printContent}
                </div>
                <script>
                    window.onload = function() {
                        // Auto-print after a short delay
                        setTimeout(function() {
                            window.print();
                            // Close window after printing or user cancels
                            setTimeout(function() {
                                window.close();
                            }, 3000);
                        }, 500);
                    };
                </script>
            </body>
        </html>
        `);
        printWindow.document.close();
    };

    return (
        <Button
            variant="contained"
            color="secondary"
            startIcon={<Print />}
            onClick={handlePrint}
            disabled={disabled}
            fullWidth
            sx={{ 
                mt: 1, 
                py: 1.5, 
                fontWeight: 'bold', 
                ...sx 
            }}
        >
            Print Receipt
        </Button>
    );
};

export default PrintReceipt;